import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
    ShieldCheck,
    Clock,
    HeartHandshake,
    GraduationCap,
    BookOpen,
    Award,
    Mail,
    MessageCircle,
    Check,
    X,
    Play,
    ChevronRight,
    Sparkles,
    HeartPulse,
    Compass,
    BadgeIndianRupee,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import CheckoutDialog from "@/components/CheckoutDialog";
import StickyCTA from "@/components/StickyCTA";
import InlineCTA from "@/components/InlineCTA";
import Testimonial from "@/components/Testimonial";
import { PARENT_TESTIMONIALS } from "@/content/testimonials";
import useLenis from "@/hooks/useLenis";
import usePriceLabel from "@/hooks/usePriceLabel";
import { Reveal, MaskedLines, Marquee } from "@/components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Brand assets
// ---------------------------------------------------------------------------
const TCC_LOGO_MARK = "/tcc_logo_mark.png";

// Hemangi's photoset (same shoot, navy backdrop, black blazer + teal blouse).
const HEMANGI_PORTRAIT_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/8it1706q_1000441617.jpg"; // Image 1 — small in hero trust-strip + secondary in credibility
const HEMANGI_CREDIBILITY_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/dlb5zf68_1000441619.jpg"; // Image 3 — main credibility (Section 5)
const HEMANGI_COMPARISON_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/orlhkgqa_1000441620.jpg"; // Image 4 — comparison (Section 6)
const HEMANGI_CLOSING_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/ntual2is_1000441618.jpg"; // Image 2 — closing CTA (Section 10)

// ---------------------------------------------------------------------------
// STUDENT IMAGERY — final assets (uploaded by founder, Dec 2025).
// ---------------------------------------------------------------------------
// Hero — Indian teen girl at her desk, looking toward window light, smiling.
const STUDENT_HERO_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/iqgb9sow_Gemini_Generated_Image_2zb2dv2zb2dv2zb2.jpeg";
// Bridge band — 3-in-1 strip (girl studying · girl sketching · boy outdoors confident).
const STUDENT_BRIDGE_STRIP_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/lrskk6tx_Gemini_Generated_Image_51m30c51m30c51m3.jpeg";
// Warm family band before closing CTA — teen + mother reading the report together.
const FAMILY_WARM_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/th5x7u0m_Gemini_Generated_Image_c1wjy1c1wjy1c1wj.jpeg";

// Sample report — multi-page cascade (real deliverable, not a placeholder).
const SAMPLE_REPORT_IMG =
    "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/vwoujpnq_AcademiQ_Sample_Report_MultiPage.png";

export default function LandingV3() {
    const [open, setOpen] = useState(false);
    const [testimonials, setTestimonials] = useState(PARENT_TESTIMONIALS);
    const [banner, setBanner] = useState(null);
    const openCheckout = () => setOpen(true);
    const priceLabel = usePriceLabel();
    useLenis();

    useEffect(() => {
        axios
            .get(`${API}/testimonials`)
            .then(({ data }) => {
                if (Array.isArray(data) && data.length) setTestimonials(data);
            })
            .catch(() => {});
        axios
            .get(`${API}/config/public`)
            .then(({ data }) => setBanner(data.banner || null))
            .catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-aq-cream text-aq-charcoal pb-28 md:pb-0 overflow-x-hidden">
            {/* ============ Launch banner (driven by backend — auto-hides when
                 no featured coupon is active). ============ */}
            {banner?.text && (
                <div
                    className="bg-aq-navy text-aq-cream text-center py-2.5 px-4 text-xs sm:text-sm border-b border-aq-gold/30"
                    data-testid="launch-banner"
                >
                    <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 flex-wrap">
                        <Sparkles className="w-3.5 h-3.5 text-aq-gold shrink-0" />
                        <BannerText text={banner.text} code={banner.code} />
                    </div>
                </div>
            )}

            {/* ============ 1. HERO — outcome-led, student imagery ============ */}
            <header className="relative overflow-hidden">
                <div
                    aria-hidden
                    className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-aq-gold/15 blur-3xl"
                />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-aq-navy/5 blur-3xl"
                />

                <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-7 md:pt-10 pb-12 md:pb-20">
                    {/* Brand lockup */}
                    <div className="flex items-center gap-3 mb-8 md:mb-10" data-testid="brand-lockup">
                        <img
                            src={TCC_LOGO_MARK}
                            alt="TheCounselCo"
                            className="w-12 h-12 md:w-14 md:h-14 object-contain"
                            data-testid="brand-logo-mark"
                        />
                        <div className="leading-tight">
                            <div className="font-serif text-2xl text-aq-navy tracking-tight">
                                Academi<span className="text-aq-gold">Q</span>
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-aq-muted">
                                by TheCounselCo
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                        {/* Left: outcome-led copy */}
                        <div className="aq-fade-up md:col-span-6 min-w-0">
                            <motion.p
                                className="inline-flex items-center gap-2 text-[13px] sm:text-sm font-semibold uppercase tracking-[0.2em] text-aq-navy bg-aq-gold/20 border border-aq-gold/40 rounded-full px-3.5 py-1.5 mb-5"
                                data-testid="hero-eyebrow"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <span className="w-2 h-2 rounded-full bg-aq-gold" />
                                For parents of students in Grades 7–12
                            </motion.p>
                            <h1
                                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-aq-navy leading-[1.05] mb-5"
                                data-testid="hero-headline"
                            >
                                <MaskedLines
                                    lines={["Does your child know", "what they're"]}
                                    stagger={0.1}
                                    delay={0.1}
                                />
                                <span className="block overflow-hidden">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <span className="italic text-aq-gold">actually</span> good at?
                                    </motion.span>
                                </span>
                            </h1>
                            <motion.p
                                className="text-lg text-aq-muted leading-relaxed mb-7 max-w-xl"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            >
                                Most students pick a stream or career by guesswork. Give yours real
                                clarity — a science-based profile of their strengths, built by a
                                counsellor.
                            </motion.p>

                            <Button
                                onClick={openCheckout}
                                data-testid="hero-cta-button"
                                className="w-full sm:w-auto h-14 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base shadow-[0_10px_30px_-12px_rgba(200,150,44,0.6)] hover:shadow-[0_14px_36px_-12px_rgba(200,150,44,0.8)] hover:-translate-y-0.5 active:scale-[0.99] transition-all"
                            >
                                Discover your child's strengths — {priceLabel}
                                <ChevronRight className="w-5 h-5 ml-1" />
                            </Button>

                            {/* Micro-trust strip — small mention of Hemangi */}
                            <div
                                className="mt-5 flex items-center gap-3 max-w-lg"
                                data-testid="hero-trust-strip"
                            >
                                <img
                                    src={HEMANGI_PORTRAIT_IMG}
                                    alt="Hemangi Shah — Profile Building Specialist"
                                    className="w-11 h-11 rounded-full object-cover border-2 border-aq-gold/60 shadow-sm"
                                    data-testid="hero-hemangi-thumb"
                                />
                                <p className="text-sm text-aq-muted leading-snug">
                                    <Clock className="inline w-4 h-4 mr-1 -mt-0.5 text-aq-navy" />
                                    Takes about 30 minutes · Every report personally reviewed by{" "}
                                    <span className="text-aq-navy font-semibold">
                                        Hemangi Shah
                                    </span>
                                    , UCLA-certified counsellor.
                                </p>
                            </div>

                            <p className="text-sm text-aq-muted mt-6 max-w-lg" data-testid="hero-quick-cta">
                                Prefer the short version?{" "}
                                <button
                                    onClick={openCheckout}
                                    className="text-aq-gold font-semibold underline underline-offset-4 hover:text-aq-navy transition-colors"
                                    data-testid="hero-quick-cta-link"
                                >
                                    Start the assessment for {priceLabel}
                                </button>
                                . About 30 minutes, delivered in 24–48 hours.
                            </p>
                        </div>

                        {/* Right: student hero image (NOT founder) */}
                        <div className="order-first md:order-last md:col-span-6 min-w-0">
                            <motion.div
                                className="relative max-w-xl mx-auto"
                                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={{ y: -6 }}
                            >
                                <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-aq-gold/25 to-aq-navy/15 blur-2xl -z-10" />
                                <div className="rounded-[1.75rem] overflow-hidden border border-aq-border shadow-xl bg-white">
                                    <div className="aspect-[4/5] bg-aq-cream-warm overflow-hidden">
                                        <motion.img
                                            src={STUDENT_HERO_IMG}
                                            alt="A hopeful student looking ahead — clarity for Grades 7–12"
                                            className="w-full h-full object-cover"
                                            style={{ objectPosition: "65% 35%" }}
                                            data-testid="hero-student-photo"
                                            initial={{ scale: 1.15 }}
                                            animate={{ scale: 1 }}
                                            transition={{ duration: 1.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                        />
                                    </div>
                                </div>
                                {/* small floating badge */}
                                <motion.div
                                    className="absolute -bottom-4 left-6 right-6 sm:left-auto sm:right-6 sm:max-w-xs bg-white/95 backdrop-blur rounded-2xl border border-aq-border px-4 py-3 shadow-lg"
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.7, delay: 0.9 }}
                                >
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-aq-muted">
                                        Personalised PDF report
                                    </div>
                                    <div className="font-serif text-aq-navy text-base leading-tight">
                                        Real clarity for Grades 7–12.
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ============ Editorial marquee ============ */}
            <div className="bg-aq-navy text-aq-cream/80 py-4 border-y border-aq-cream/10 font-serif text-lg md:text-xl">
                <Marquee
                    items={[
                        "8 Multiple Intelligences",
                        "Real World Quotient Test",
                        "Personally reviewed by Hemangi Shah",
                        "Grades 7–12 · All boards",
                        "Career & stream clarity",
                    ]}
                />
            </div>

            {/* ============ 2. PROBLEM ============ */}
            <section className="bg-aq-navy text-aq-cream" data-testid="section-problem">
                <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold-soft uppercase tracking-[0.2em] text-xs mb-4">
                        Does this sound familiar?
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl text-aq-cream leading-tight mb-10 md:mb-12">
                        You want the right next step. <br className="hidden md:block" />
                        Guesswork won't give it to you.
                    </h2>
                    <ul className="space-y-8 md:space-y-10">
                        {[
                            "Science, Commerce or Arts — and what if we choose wrong?",
                            "Is my child actually suited to the career we have in mind?",
                            "What are they naturally good at — beyond marks?",
                        ].map((q, i) => (
                            <Reveal key={q} delay={i * 0.1}>
                                <li className="flex gap-5 md:gap-7 items-start border-b border-aq-cream/10 pb-8 md:pb-10">
                                    <span className="font-serif text-aq-gold/70 text-3xl md:text-5xl leading-none shrink-0 tabular-nums">
                                        0{i + 1}
                                    </span>
                                    <span className="text-xl md:text-3xl font-serif text-aq-cream leading-snug">
                                        "{q}"
                                    </span>
                                </li>
                            </Reveal>
                        ))}
                    </ul>
                    <p className="mt-10 text-base md:text-lg text-aq-cream/70 border-l-2 border-aq-gold pl-5 italic">
                        Generic online quizzes won't tell you. A real assessment will.
                    </p>
                </div>
            </section>

            {/* ============ 3. WHAT YOU GET + parent benefits ============ */}
            <section className="bg-aq-cream" data-testid="section-what-you-get">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
                    <div>
                        <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-4">
                            What you actually get
                        </p>
                        <h2 className="font-serif text-3xl md:text-5xl text-aq-navy leading-tight mb-5">
                            For {priceLabel}, a complete personalised report.
                        </h2>
                        <p className="text-aq-muted text-lg mb-8">
                            Your child takes the AcademiQ Assessment online. You receive a beautiful,
                            personally-reviewed PDF on your WhatsApp and email.
                        </p>

                        <ul className="space-y-5">
                            {[
                                {
                                    icon: <Sparkles className="w-5 h-5" />,
                                    title: "8 Multiple Intelligences",
                                    body: "How your child is wired to learn.",
                                },
                                {
                                    icon: <HeartHandshake className="w-5 h-5" />,
                                    title: "The Real World Quotient Test",
                                    body: "Vision, resilience, creativity, emotional & digital strengths.",
                                },
                                {
                                    icon: <BookOpen className="w-5 h-5" />,
                                    title: "Plain-English meaning",
                                    body: "What it all means for their future — no jargon.",
                                },
                                {
                                    icon: <GraduationCap className="w-5 h-5" />,
                                    title: "Clear stream & career directions",
                                    body: "Personalised, career-specific paths actually worth exploring.",
                                },
                            ].map((b) => (
                                <li key={b.title} className="flex gap-4">
                                    <div className="w-11 h-11 rounded-full bg-aq-navy text-aq-gold flex items-center justify-center shrink-0">
                                        {b.icon}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-aq-navy text-lg leading-tight">
                                            {b.title}
                                        </div>
                                        <div className="text-aq-muted">{b.body}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <p className="mt-8 text-aq-charcoal">
                            Delivered as a beautiful PDF —{" "}
                            <span className="font-semibold text-aq-navy">
                                personally reviewed by Hemangi.
                            </span>
                        </p>
                    </div>

                    {/* Sample-report — real deliverable preview */}
                    <div className="relative">
                        <div className="absolute -inset-4 bg-gradient-to-br from-aq-gold/20 to-aq-navy/10 blur-2xl rounded-[2rem] -z-10" />
                        <div
                            className="relative rounded-3xl overflow-hidden border border-aq-border shadow-xl bg-white"
                            data-testid="sample-report-image"
                        >
                            <div className="relative bg-aq-cream-warm p-3 md:p-5">
                                <img
                                    src={SAMPLE_REPORT_IMG}
                                    alt="Sample AcademiQ report — Multiple Intelligences profile + Real World Quotient Test + counsellor's notes"
                                    className="w-full h-auto object-contain"
                                />
                                <div className="absolute top-5 right-5 bg-aq-gold text-aq-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-sm">
                                    PDF · 24–48 hrs
                                </div>
                            </div>
                            <div className="border-t border-aq-border px-5 py-4 bg-white">
                                <div className="text-[11px] uppercase tracking-[0.16em] text-aq-muted">
                                    Sample report preview
                                </div>
                                <div className="font-serif text-aq-navy text-lg leading-snug">
                                    Your child's personalised AcademiQ profile.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* PARENT BENEFITS strip (folded-in from counsellor notes) */}
                <div className="max-w-6xl mx-auto px-5 md:px-8 pb-16 md:pb-24" data-testid="parent-benefits">
                    <div className="border-t border-aq-border pt-12">
                        <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3 text-center">
                            What this gives you as a parent
                        </p>
                        <h3 className="font-serif text-2xl md:text-3xl text-aq-navy text-center leading-tight mb-10 max-w-3xl mx-auto">
                            Beyond the report — calmer decisions, clearer direction.
                        </h3>
                        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
                            {[
                                {
                                    icon: <HeartPulse className="w-5 h-5" />,
                                    title: "A calmer relationship with your child",
                                    body: "Built on understanding how they're wired — not on marks.",
                                },
                                {
                                    icon: <Compass className="w-5 h-5" />,
                                    title: "A structured path for career choices",
                                    body: "Not guesswork — a clear, evidence-based direction to discuss.",
                                },
                                {
                                    icon: <BadgeIndianRupee className="w-5 h-5" />,
                                    title: "Quality counsellor-led guidance",
                                    body: "Personally reviewed by Hemangi, at an honest, accessible price.",
                                },
                            ].map((b, i) => (
                                <div
                                    key={b.title}
                                    className="rounded-3xl bg-white border border-aq-border p-6 md:p-7 shadow-sm"
                                    data-testid={`parent-benefit-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-full bg-aq-gold/15 text-aq-gold flex items-center justify-center mb-4">
                                        {b.icon}
                                    </div>
                                    <h4 className="font-serif text-lg text-aq-navy mb-2 leading-tight">
                                        {b.title}
                                    </h4>
                                    <p className="text-aq-muted text-sm leading-relaxed">
                                        {b.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ Inline CTA — after "What you get + Parent benefits" ============ */}
            <InlineCTA
                testId="inline-cta-after-benefits"
                variant="cream"
                eyebrow="Ready when you are"
                headline={`Give your child the ${priceLabel} report their future deserves.`}
                subline="Delivered to your WhatsApp & email within 24–48 hours, personally reviewed by Hemangi."
                ctaLabel={`Start now — ${priceLabel}`}
                onClick={openCheckout}
            />

            {/* ============ 4. EMOTIONAL BRIDGE — student imagery ============ */}
            <section className="bg-aq-cream-warm" data-testid="section-bridge">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                        A glimpse of their future
                    </p>
                    <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                        <div className="md:col-span-6">
                            <h2 className="font-serif text-3xl md:text-5xl text-aq-navy leading-[1.1] mb-6">
                                The next decade gets clearer when a child knows their own strengths
                                early.
                            </h2>
                            <p className="text-aq-muted text-lg leading-relaxed max-w-xl">
                                When a child understands how they're wired, choosing the right
                                subjects, the right direction — and even daily conversations at home
                                — gets calmer. Less guesswork, less anxiety, more momentum.
                            </p>
                        </div>
                        <div className="md:col-span-6">
                            <div
                                className="relative rounded-3xl overflow-hidden border border-aq-border bg-white shadow-xl"
                                data-testid="bridge-strip"
                            >
                                <div className="aspect-[3/2]">
                                    <img
                                        src={STUDENT_BRIDGE_STRIP_IMG}
                                        alt="Students building their future — studying, creating, looking ahead"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-aq-muted/80 italic mt-3 text-center">
                                Study · Create · Look ahead — three students, one clearer path each.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 5. CREDIBILITY — Hemangi (mid-page reason to believe) ============ */}
            <section className="bg-aq-cream" data-testid="section-credibility">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-5 gap-10 md:gap-14 items-center">
                    {/* Hemangi photo (Image 3 — main) */}
                    <div className="md:col-span-2">
                        <div className="relative max-w-sm mx-auto md:mx-0">
                            <div className="absolute -inset-3 rounded-[2rem] bg-aq-navy/10 blur-2xl -z-10" />
                            <div className="rounded-3xl overflow-hidden border border-aq-border shadow-xl bg-white">
                                <div className="aspect-[4/5]">
                                    <img
                                        src={HEMANGI_CREDIBILITY_IMG}
                                        alt="Hemangi Shah — Profile Building Specialist"
                                        className="w-full h-full object-cover"
                                        data-testid="credibility-hemangi-photo"
                                    />
                                </div>
                                <div className="px-5 py-4 bg-white">
                                    <div className="text-xs uppercase tracking-[0.16em] text-aq-muted">
                                        Profile Building Specialist
                                    </div>
                                    <div className="font-serif text-xl text-aq-navy">
                                        Hemangi Shah
                                    </div>
                                </div>
                            </div>

                            {/* Hemangi intro video */}
                            <IntroVideo />
                        </div>
                    </div>

                    {/* Copy */}
                    <div className="md:col-span-3">
                        <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                            Why you can trust this report
                        </p>
                        <h2 className="font-serif text-3xl md:text-5xl text-aq-navy leading-tight mb-2">
                            Built by Hemangi Shah.
                        </h2>
                        <p className="text-aq-muted text-base mb-6 italic">
                            Profile Building Specialist · Founder, TheCounselCo
                        </p>

                        <ul className="space-y-3 mb-6">
                            {[
                                {
                                    icon: <Award className="w-4 h-4" />,
                                    text: "UCLA-certified career counsellor · Cambridge-trained educator",
                                },
                                {
                                    icon: <GraduationCap className="w-4 h-4" />,
                                    text: "10+ years' experience · mentored 1,000+ students across ICSE, IB & IGCSE",
                                },
                                {
                                    icon: <BookOpen className="w-4 h-4" />,
                                    text: "Founder, TheCounselCo · Author, Beyond School – Be Future Ready",
                                },
                            ].map((c) => (
                                <li key={c.text} className="flex items-start gap-3 text-aq-charcoal">
                                    <span className="mt-1 w-6 h-6 rounded-full bg-aq-navy text-aq-gold flex items-center justify-center shrink-0">
                                        {c.icon}
                                    </span>
                                    <span className="text-base md:text-lg leading-snug">
                                        {c.text}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="bg-white border border-aq-border rounded-2xl p-5 md:p-6 shadow-sm">
                            <ShieldCheck className="w-6 h-6 text-aq-gold mb-2" />
                            <p className="text-aq-charcoal text-lg leading-relaxed">
                                <span className="font-semibold text-aq-navy">
                                    Every report is personally reviewed by Hemangi
                                </span>{" "}
                                — not auto-generated and forgotten.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 6. COMPARISON ============ */}
            <section className="bg-aq-cream-warm" data-testid="section-comparison">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3 text-center">
                        Why this beats a generic test
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl text-aq-navy text-center leading-tight mb-12">
                        Not your average online quiz.
                    </h2>

                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Hemangi — confident arms-crossed portrait */}
                        <div className="lg:col-span-4 order-last lg:order-first">
                            <div className="relative max-w-xs mx-auto">
                                <div className="absolute -inset-3 rounded-[2rem] bg-aq-gold/15 blur-2xl -z-10" />
                                <div className="rounded-3xl overflow-hidden border border-aq-border shadow-xl bg-white">
                                    <div className="aspect-[4/5]">
                                        <img
                                            src={HEMANGI_COMPARISON_IMG}
                                            alt="Hemangi Shah — confident, arms crossed"
                                            className="w-full h-full object-cover"
                                            data-testid="comparison-hemangi-photo"
                                        />
                                    </div>
                                    <div className="px-4 py-3 bg-white">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-aq-muted">
                                            Profile Building Specialist
                                        </div>
                                        <div className="font-serif text-lg text-aq-navy leading-tight">
                                            Hemangi Shah
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-aq-muted mt-4 leading-relaxed italic">
                                    "A real counsellor's read of your child — not a generic
                                    algorithm's."
                                </p>
                            </div>
                        </div>

                        <div className="lg:col-span-8">
                            <div className="overflow-hidden rounded-3xl border border-aq-border bg-white shadow-sm">
                                <div className="grid grid-cols-3 text-sm md:text-base">
                                    <div className="p-4 md:p-6 bg-aq-cream-warm/60 font-semibold text-aq-muted">
                                        &nbsp;
                                    </div>
                                    <div className="p-4 md:p-6 bg-aq-cream-warm/60 font-semibold text-aq-muted">
                                        Generic test
                                    </div>
                                    <div className="p-4 md:p-6 bg-aq-navy text-aq-gold font-bold uppercase tracking-wider text-xs md:text-sm">
                                        AcademiQ
                                    </div>

                                    {[
                                        ["What it measures", "One cognitive score", "8 Intelligences + Real World Quotient Test"],
                                        ["Built by", "Generic algorithm", "A real counsellor (Hemangi Shah)"],
                                        ["The report", "Automated, impersonal", "Personally reviewed by Hemangi"],
                                        ["Career directions", "Vague", "Clear, personalised, plain English"],
                                        ["Designed for", "Often adults", "Students, Grades 7–12"],
                                    ].map(([label, generic, aq], idx) => (
                                        <RowGroup
                                            key={label}
                                            label={label}
                                            generic={generic}
                                            aq={aq}
                                            alt={idx % 2 === 1}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 7. HOW IT WORKS ============ */}
            <section className="bg-aq-cream" data-testid="section-how-it-works">
                <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3 text-center">
                        How it works
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl text-aq-navy text-center leading-tight mb-12">
                        Three simple steps.
                    </h2>

                    <div className="grid md:grid-cols-3 gap-5 md:gap-7">
                        {[
                            {
                                n: "01",
                                title: "Pay & take the assessment",
                                body: `${priceLabel} secure payment, then your child takes the ~30-minute online test from home.`,
                                cta: true,
                            },
                            {
                                n: "02",
                                title: "Hemangi reviews personally",
                                body: "Hemangi personally reviews your child's profile — not a script, not an algorithm.",
                            },
                            {
                                n: "03",
                                title: "Receive the full report",
                                body: "Delivered to your WhatsApp & email within 24–48 hours. Clear, kind, actionable.",
                            },
                        ].map((s) => (
                            <div
                                key={s.n}
                                className="rounded-3xl bg-white border border-aq-border p-6 md:p-7 shadow-sm flex flex-col"
                            >
                                <div className="font-serif text-aq-gold text-3xl mb-3">{s.n}</div>
                                <h3 className="font-serif text-xl text-aq-navy mb-2 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-aq-muted leading-relaxed mb-4">{s.body}</p>
                                {s.cta && (
                                    <div className="mt-auto pt-4 border-t border-aq-border">
                                        <Button
                                            onClick={openCheckout}
                                            data-testid="how-it-works-cta"
                                            className="w-full h-12 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-md hover:shadow-lg active:scale-[0.99] transition-all"
                                        >
                                            Pay & start now — {priceLabel}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 8. TESTIMONIALS ============ */}
            <section className="bg-aq-cream-warm" data-testid="section-testimonials">
                <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3 text-center">
                        Parent voices
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl text-aq-navy text-center leading-tight mb-12">
                        From parents who took the step.
                    </h2>

                    <div className="grid md:grid-cols-2 gap-6 items-start">
                        {testimonials.map((t, i) => (
                            <Reveal key={t.id || t.name} delay={i * 0.08}>
                                <Testimonial {...t} testId={`testimonial-${i + 1}`} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ 9. FAQ ============ */}
            <section className="bg-aq-cream" data-testid="section-faq">
                <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3 text-center">
                        FAQ
                    </p>
                    <h2 className="font-serif text-3xl md:text-5xl text-aq-navy text-center leading-tight mb-10">
                        Quick answers.
                    </h2>

                    <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                        <FaqGroup label="About the assessment" />
                        {[
                            {
                                q: "Who is this for?",
                                a: "Students in Grades 7–12 (roughly ages 12–18), across all boards — CBSE, ICSE, IB and IGCSE. Most useful when stream and subject decisions are being made.",
                            },
                            {
                                q: "Is this an IQ or aptitude test?",
                                a: "No. A standard aptitude test gives one cognitive score. AcademiQ maps how your child actually thinks and learns across 8 Multiple Intelligences and the Real World Quotient Test — a much fuller picture.",
                            },
                            {
                                q: "What is the Real World Quotient Test?",
                                a: "It measures dimensions a normal test ignores — vision, resilience (adversity), creativity, emotional, digital and experiential strengths — how your child engages with the real world, not just exams.",
                            },
                            {
                                q: "How long does the test take?",
                                a: "Around 30 minutes, online from home, on a phone or laptop. No special setup needed.",
                            },
                            {
                                q: "Does my child need to prepare?",
                                a: "No. There are no right or wrong answers — it's about how your child naturally thinks, so it works best when they answer honestly and without preparation.",
                            },
                        ].map((item, i) => (
                            <FaqItem key={item.q} item={item} i={i} />
                        ))}

                        <FaqGroup label="About the report" />
                        {[
                            {
                                q: "What exactly do I receive?",
                                a: "A complete, personalised PDF report covering your child's 8 Intelligences, Real World Quotient Test results, key strengths and growth areas, plus plain-English career and stream directions worth exploring.",
                            },
                            {
                                q: "Is it really reviewed by a person?",
                                a: "Yes. Every report is personally reviewed by Hemangi Shah — UCLA-certified counsellor and founder of TheCounselCo — before it's sent. It is not auto-generated and forgotten.",
                            },
                            {
                                q: "When and how do I get it?",
                                a: "On your WhatsApp number and email, within 24–48 hours of your child completing the assessment.",
                            },
                            {
                                q: "What if I have questions after reading the report?",
                                a: "You can email us and we'll respond — we want you to actually understand and use your child's profile.",
                            },
                        ].map((item, i) => (
                            <FaqItem key={item.q} item={item} i={i + 5} />
                        ))}

                        <FaqGroup label="About payment & trust" />
                        {[
                            {
                                q: "Is the payment secure?",
                                a: "Yes. Payment is processed by Razorpay (UPI, card or netbanking) — we never see or store your card details.",
                            },
                            {
                                q: "What happens right after I pay?",
                                a: "You're taken straight to the online assessment. Your child can take it immediately, or you can return to the same link later.",
                            },
                            {
                                q: "Will I get a refund if I change my mind?",
                                a: "A full refund is available if you cancel before your child starts the assessment. Once the assessment has been taken, the report work begins, so it cannot be refunded.",
                            },
                            {
                                q: "Is my family's data private?",
                                a: "Yes. Your contact details and your child's responses are used only to prepare and deliver the report, and are never sold or shared.",
                            },
                            {
                                q: "Can I do this for more than one child?",
                                a: "Yes — each child needs a separate assessment so the report is personal to them.",
                            },
                        ].map((item, i) => (
                            <FaqItem key={item.q} item={item} i={i + 9} />
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* ============ Warm family band — bridge into final CTA ============ */}
            <section className="bg-aq-cream-warm" data-testid="section-family-band">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
                    <div className="grid md:grid-cols-12 rounded-3xl overflow-hidden border border-aq-border bg-white shadow-xl">
                        {/* Image side */}
                        <div className="md:col-span-7 order-2 md:order-1">
                            <div className="aspect-[5/4] md:aspect-auto md:h-full">
                                <img
                                    src={FAMILY_WARM_IMG}
                                    alt="A parent and her teen reading the AcademiQ report together"
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: "center 30%" }}
                                    data-testid="family-warm-image"
                                />
                            </div>
                        </div>
                        {/* Text side — solid navy block */}
                        <div className="md:col-span-5 order-1 md:order-2 bg-aq-navy text-aq-cream p-7 md:p-10 flex flex-col justify-center">
                            <p className="text-aq-gold-soft uppercase tracking-[0.2em] text-[11px] md:text-xs mb-3">
                                Built for conversations like these
                            </p>
                            <h3 className="font-serif text-2xl md:text-3xl leading-snug mb-3 text-aq-cream">
                                From a confused conversation at the dining table — to a clearer
                                decade ahead.
                            </h3>
                            <p className="text-aq-cream/70 text-sm leading-relaxed">
                                The AcademiQ report gives your family something concrete to read
                                together, talk about, and act on — calmly.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ 10. FINAL CLOSE ============ */}
            <section
                className="bg-aq-navy text-aq-cream relative overflow-hidden"
                data-testid="section-final-cta"
            >
                <div
                    aria-hidden
                    className="absolute -top-32 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-aq-gold/10 blur-3xl"
                />
                <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28 grid md:grid-cols-5 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-2 order-last md:order-first">
                        <div className="relative max-w-xs mx-auto">
                            <div className="absolute -inset-3 rounded-[2rem] bg-aq-gold/20 blur-2xl -z-10" />
                            <div className="rounded-3xl overflow-hidden border border-aq-cream/15 shadow-2xl">
                                <div className="aspect-[4/5]">
                                    <img
                                        src={HEMANGI_CLOSING_IMG}
                                        alt="Hemangi Shah — warm, relaxed"
                                        className="w-full h-full object-cover"
                                        data-testid="closing-hemangi-photo"
                                    />
                                </div>
                            </div>
                            <p className="text-aq-cream/70 text-sm mt-4 italic leading-relaxed text-center">
                                "Every report is personally reviewed by me — before it reaches you."
                                <br />
                                <span className="text-aq-gold-soft not-italic font-semibold">
                                    — Hemangi
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="md:col-span-3 text-center md:text-left">
                        <p className="text-aq-gold-soft uppercase tracking-[0.2em] text-xs mb-4">
                            Start today
                        </p>
                        <h2 className="font-serif text-4xl md:text-6xl text-aq-cream leading-[1.05] mb-6">
                            Give your child clarity.
                        </h2>
                        <p className="text-aq-cream/80 text-lg max-w-xl mb-8 mx-auto md:mx-0">
                            One flat fee. One personally-reviewed report. Real direction for the
                            years ahead.
                        </p>

                        <Button
                            onClick={openCheckout}
                            data-testid="final-cta-button"
                            className="h-14 md:h-16 px-10 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base md:text-lg shadow-[0_14px_36px_-12px_rgba(200,150,44,0.7)] hover:-translate-y-0.5 transition-all"
                        >
                            Take the Assessment — {priceLabel}
                        </Button>

                        <p className="text-aq-cream/70 text-sm mt-5 max-w-md leading-relaxed mx-auto md:mx-0">
                            <ShieldCheck className="inline w-4 h-4 mr-1 -mt-0.5 text-aq-gold-soft" />
                            Secure payment via UPI / card / netbanking · Report delivered to your
                            WhatsApp & email within 24–48 hours.
                        </p>
                        <p className="text-aq-cream/50 text-xs mt-3">
                            After payment, you'll be taken straight to the assessment.
                        </p>
                    </div>
                </div>
            </section>

            {/* ============ 11. FOOTER ============ */}
            <footer className="bg-aq-navy-deep text-aq-cream/70" data-testid="footer">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
                    <div className="grid md:grid-cols-3 gap-10">
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <img
                                    src={TCC_LOGO_MARK}
                                    alt="TheCounselCo"
                                    className="w-10 h-10 object-contain bg-white/95 rounded-lg p-1"
                                />
                                <div className="leading-tight">
                                    <div className="font-serif text-2xl text-aq-cream">
                                        Academi<span className="text-aq-gold">Q</span>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-aq-cream/50">
                                        by TheCounselCo
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm leading-relaxed text-aq-cream/60">
                                A counsellor-built assessment for students in Grades 7–12.
                                Personalised, career-specific reports — personally reviewed by
                                Hemangi Shah.
                            </p>
                        </div>

                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-aq-cream/50 mb-3">
                                Contact
                            </div>
                            <ul className="space-y-2.5 text-sm">
                                <li className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-aq-gold shrink-0" />
                                    <a
                                        href="mailto:thecounselco@gmail.com"
                                        className="hover:text-aq-cream break-all"
                                        data-testid="footer-email"
                                    >
                                        thecounselco@gmail.com
                                    </a>
                                </li>
                                <li className="flex items-center gap-2">
                                    <MessageCircle className="w-4 h-4 text-aq-gold shrink-0" />
                                    <a
                                        href="https://wa.me/919819945964"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:text-aq-cream"
                                        data-testid="footer-whatsapp"
                                    >
                                        +91 98199 45964 (WhatsApp)
                                    </a>
                                </li>
                                <li className="text-xs text-aq-cream/50 leading-relaxed pt-1">
                                    Reports are delivered to the WhatsApp number and email you
                                    provide at checkout.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-aq-cream/50 mb-3">
                                Ready to start?
                            </div>
                            <button
                                onClick={openCheckout}
                                data-testid="footer-cta-link"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-lg hover:-translate-y-0.5 transition-all"
                            >
                                Start now — {priceLabel}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <p className="text-xs text-aq-cream/50 mt-3 leading-relaxed max-w-[240px]">
                                Secure Razorpay payment. Report on WhatsApp & email within 24–48 hours.
                            </p>
                        </div>
                    </div>

                    <div className="border-t border-aq-cream/10 mt-10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-aq-cream/50">
                        <div>
                            © {new Date().getFullYear()} TheCounselCo. Reports personally reviewed by
                            Hemangi Shah.
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2">
                            <Link
                                to="/privacy"
                                className="hover:text-aq-cream"
                                data-testid="footer-link-privacy"
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/terms"
                                className="hover:text-aq-cream"
                                data-testid="footer-link-terms"
                            >
                                Terms & Conditions
                            </Link>
                            <Link
                                to="/refund"
                                className="hover:text-aq-cream"
                                data-testid="footer-link-refund"
                            >
                                Refund Policy
                            </Link>
                            <Link
                                to="/cookies"
                                className="hover:text-aq-cream"
                                data-testid="footer-link-cookies"
                            >
                                Cookie Policy
                            </Link>
                            <Link
                                to="/contact"
                                className="hover:text-aq-cream"
                                data-testid="footer-link-contact"
                            >
                                Contact
                            </Link>
                        </div>
                    </div>

                    <p
                        className="text-[11px] text-aq-cream/40 leading-relaxed mt-6 max-w-3xl"
                        data-testid="footer-disclaimer"
                    >
                        AcademiQ is a guidance and profiling tool for educational purposes only —
                        not a clinical or diagnostic assessment. Reports offer directions to
                        explore, not guarantees of outcomes.
                    </p>
                </div>
            </footer>

            <StickyCTA onClick={openCheckout} />
            <CheckoutDialog open={open} onOpenChange={setOpen} />
        </div>
    );
}

// helpers -----------------------------------------------------------------
function IntroVideo() {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const handlePlay = () => {
        setPlaying(true);
        videoRef.current?.play();
    };

    return (
        <div className="mt-6" data-testid="intro-video-card">
            <div className="text-[11px] uppercase tracking-[0.18em] text-aq-muted mb-2.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-aq-gold" />A quick hello from
                Hemangi
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-aq-border shadow-xl bg-aq-navy group">
                <video
                    ref={videoRef}
                    poster="/videos/hemangi-intro-poster.jpg"
                    className="w-full aspect-video object-cover"
                    controls={playing}
                    playsInline
                    preload="none"
                    data-testid="intro-video-player"
                >
                    <source src="/videos/hemangi-intro.webm" type="video/webm" />
                    <source src="/videos/hemangi-intro.mp4" type="video/mp4" />
                </video>
                {!playing && (
                    <button
                        onClick={handlePlay}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-aq-navy/25 hover:bg-aq-navy/10 transition-colors"
                        data-testid="intro-video-play-button"
                        aria-label="Play Hemangi's intro video"
                    >
                        <span className="w-16 h-16 rounded-full bg-aq-gold text-aq-navy flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(200,150,44,0.8)] group-hover:scale-105 transition-transform">
                            <Play className="w-7 h-7 ml-0.5" fill="currentColor" />
                        </span>
                        <span className="text-white text-xs sm:text-sm font-semibold bg-aq-navy/70 backdrop-blur px-3.5 py-1.5 rounded-full">
                            Watch the 2-min intro
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
}

function RowGroup({ label, generic, aq, alt }) {
    const base = "p-4 md:p-6 border-t border-aq-border text-aq-charcoal";
    const altBg = alt ? "bg-aq-cream/40" : "";
    return (
        <>
            <div className={`${base} ${altBg} font-semibold text-aq-navy`}>{label}</div>
            <div className={`${base} ${altBg} text-aq-muted flex items-start gap-2`}>
                <X className="w-4 h-4 mt-1 text-aq-muted shrink-0" />
                <span>{generic}</span>
            </div>
            <div className={`${base} ${altBg} bg-aq-gold/10 text-aq-navy flex items-start gap-2 font-medium`}>
                <Check className="w-4 h-4 mt-1 text-aq-gold shrink-0" />
                <span>{aq}</span>
            </div>
        </>
    );
}

function FaqGroup({ label }) {
    return (
        <div className="pt-7 pb-2 first:pt-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-aq-gold font-semibold">
                {label}
            </p>
        </div>
    );
}

function FaqItem({ item, i }) {
    return (
        <AccordionItem
            value={`faq-${i}`}
            className="border-b border-aq-navy/15"
            data-testid={`faq-item-${i}`}
        >
            <AccordionTrigger className="text-left font-serif text-aq-navy text-lg md:text-xl py-5 hover:no-underline">
                {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-aq-muted text-base leading-relaxed pb-5">
                {item.a}
            </AccordionContent>
        </AccordionItem>
    );
}

function BannerText({ text, code }) {
    if (!text) return null;
    if (!code || !text.includes(code)) {
        return <span className="text-aq-cream/90">{text}</span>;
    }
    const [before, after] = text.split(code);
    return (
        <span className="text-aq-cream/90">
            {before}
            <span
                className="font-bold text-aq-gold tracking-wider bg-aq-gold/10 border border-aq-gold/40 rounded px-1.5 py-0.5 mx-0.5"
                data-testid="launch-banner-code"
            >
                {code}
            </span>
            {after}
        </span>
    );
}
